import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, Search } from "lucide-react";
import { HeaderPage } from "@/components/HeaderPage";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
  updateTeamSchema,
  type UpdateTeamFormData,
} from "@/schemas/teamSchemas";
import { useUpdateTeam, useGetTeamById } from "@/hooks/useTeams";
import { useGetUsers } from "@/hooks/useUsers";
import { useGetCategories } from "@/hooks/useCategories";
import { useGetSectors } from "@/hooks/useSectors";
import { MultiSelectTable } from "@/components/MultiSelectTable";
import { useEffect, useState } from "react";
import { Loading } from "@/components/Loading";

const workShiftOptions = [
  { value: "MORNING", label: "Manhã" },
  { value: "AFTERNOON", label: "Tarde" },
  { value: "NIGHT", label: "Noite" },
  { value: "FULL_TIME", label: "Integral" },
];

const statusOptions = [
  { value: "ACTIVE", label: "Ativa" },
  { value: "INACTIVE", label: "Inativa" },
];

export const EditTeam = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  console.log("ID da URL:", id); // Debug: verificar se o ID está sendo capturado
  console.log("ID é válido (!!id):", !!id); // Debug: verificar se passa na validação do enabled

  const { data: teamData, isLoading, error } = useGetTeamById(id!);

  console.log("teamData no componente:", teamData); // Debug inicial
  console.log("isLoading:", isLoading); // Debug loading state
  console.log("error:", error); // Debug error state
  const updateTeam = useUpdateTeam(id!);
  const { data: usersData } = useGetUsers();
  const { data: categoriesData } = useGetCategories();
  const { data: sectorsData } = useGetSectors();

  const [searchSector, setSearchSector] = useState("");
  const [searchLeader, setSearchLeader] = useState("");

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<UpdateTeamFormData>({
    resolver: zodResolver(updateTeamSchema),
    defaultValues: {
      status: "ACTIVE",
      memberIds: [],
      categoryIds: [],
    },
  });

  const memberIds = watch("memberIds");
  const categoryIds = watch("categoryIds");

  const filteredSectors = sectorsData?.data?.filter((sector: any) =>
    sector.name?.toLowerCase().includes(searchSector.toLowerCase()),
  );

  const filteredUsers = usersData?.data?.filter((user: any) => {
    const fullName = user.profile?.fullName || "";
    const email = user.email || "";
    return (
      fullName.toLowerCase().includes(searchLeader.toLowerCase()) ||
      email.toLowerCase().includes(searchLeader.toLowerCase())
    );
  });

  useEffect(() => {
    const actualTeamData = teamData?.data || teamData;
    console.log("actualTeamData:", actualTeamData);

    if (actualTeamData && actualTeamData.id) {
      const formData = {
        name: actualTeamData.name,
        sectorId: actualTeamData.sector?.id || actualTeamData.sectorId,
        regional: actualTeamData.regional,
        leaderId: actualTeamData.leader?.id || actualTeamData.leaderId,
        workShift: actualTeamData.workShift,
        status: actualTeamData.status,
        memberIds:
          actualTeamData.members?.map((member: any) => member.id) ||
          actualTeamData.memberIds ||
          [],
        categoryIds:
          actualTeamData.categories?.map((category: any) => category.id) ||
          actualTeamData.categoryIds ||
          [],
      };
      reset(formData);
    } else {
      console.log("Dados da equipe não encontrados ou inválidos");
    }
  }, [teamData, reset]);

  const onSubmit = async (data: UpdateTeamFormData) => {
    try {
      await updateTeam.mutateAsync(data, {
        onSuccess: () => {
          navigate("/teams");
          toast.success("Equipe atualizada com sucesso!");
        },
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Erro ao atualizar equipe";
      toast.error(errorMessage);
    }
  };

  const handleAddMember = (memberId: string) => {
    setValue("memberIds", [...memberIds, memberId]);
  };

  const handleRemoveMember = (memberId: string) => {
    setValue(
      "memberIds",
      memberIds.filter((id) => id !== memberId),
    );
  };

  const handleAddCategory = (categoryId: string) => {
    setValue("categoryIds", [...categoryIds, categoryId]);
  };

  const handleRemoveCategory = (categoryId: string) => {
    setValue(
      "categoryIds",
      categoryIds.filter((id) => id !== categoryId),
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-gray-50">
        <HeaderPage
          title="Editar Equipe"
          description="Atualize os dados da equipe"
        />
        <div className="flex-1 flex items-center justify-center">
          <Loading label="Carregando equipe..." />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <HeaderPage
        title="Editar Equipe"
        description="Atualize os dados da equipe"
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 overflow-auto mt-10"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="bg-white mx-auto">
          <Card>
            <CardContentUI className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Nome */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Nome da Equipe <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Digite o nome da equipe"
                    {...register("name")}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                {/* Setor */}
                <Controller
                  name="sectorId"
                  control={control}
                  render={({ field }) => (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Setor <span className="text-red-500">*</span>
                      </label>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o setor" />
                        </SelectTrigger>
                        <SelectContent>
                          <div className="flex items-center border-b px-3 pb-2">
                            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                            <Input
                              placeholder="Pesquisar setor..."
                              value={searchSector}
                              onChange={(e) => setSearchSector(e.target.value)}
                              className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                            />
                          </div>
                          <div className="max-h-[200px] overflow-y-auto">
                            {filteredSectors?.map((sector: any) => (
                              <SelectItem key={sector.id} value={sector.id}>
                                {sector.name}
                              </SelectItem>
                            ))}
                            {filteredSectors?.length === 0 && (
                              <div className="py-6 text-center text-sm text-gray-500">
                                Nenhum setor encontrado
                              </div>
                            )}
                          </div>
                        </SelectContent>
                      </Select>
                      {errors.sectorId && (
                        <p className="text-sm text-red-500">
                          {errors.sectorId.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                {/* Regional */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Regional <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Digite a regional"
                    {...register("regional")}
                  />
                  {errors.regional && (
                    <p className="text-sm text-red-500">
                      {errors.regional.message}
                    </p>
                  )}
                </div>

                {/* Líder */}
                <Controller
                  name="leaderId"
                  control={control}
                  render={({ field }) => (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Líder <span className="text-red-500">*</span>
                      </label>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o líder" />
                        </SelectTrigger>
                        <SelectContent>
                          <div className="flex items-center border-b px-3 pb-2">
                            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                            <Input
                              placeholder="Pesquisar líder..."
                              value={searchLeader}
                              onChange={(e) => setSearchLeader(e.target.value)}
                              className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                            />
                          </div>
                          <div className="max-h-[200px] overflow-y-auto">
                            {filteredUsers?.map((user: any) => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.profile?.fullName || user.email}
                              </SelectItem>
                            ))}
                            {filteredUsers?.length === 0 && (
                              <div className="py-6 text-center text-sm text-gray-500">
                                Nenhum líder encontrado
                              </div>
                            )}
                          </div>
                        </SelectContent>
                      </Select>
                      {errors.leaderId && (
                        <p className="text-sm text-red-500">
                          {errors.leaderId.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                {/* Turno de Trabalho */}
                <Controller
                  name="workShift"
                  control={control}
                  render={({ field }) => (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Turno de Trabalho{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o turno" />
                        </SelectTrigger>
                        <SelectContent>
                          {workShiftOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.workShift && (
                        <p className="text-sm text-red-500">
                          {errors.workShift.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                {/* Status */}
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Status <span className="text-red-500">*</span>
                      </label>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o status" />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.status && (
                        <p className="text-sm text-red-500">
                          {errors.status.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>

              {/* Membros da Equipe */}
              <div className="mt-6">
                <MultiSelectTable
                  title="Membros da Equipe"
                  items={usersData?.data || []}
                  selectedIds={memberIds}
                  onAdd={handleAddMember}
                  onRemove={handleRemoveMember}
                  searchPlaceholder="Pesquisar membros..."
                  emptyMessage="Nenhum membro adicionado"
                  displayField={(user) => user.profile?.fullName || "Sem nome"}
                />
              </div>

              {/* Categorias */}
              <div className="mt-6">
                <MultiSelectTable
                  title="Categorias"
                  items={categoriesData?.data || []}
                  selectedIds={categoryIds}
                  onAdd={handleAddCategory}
                  onRemove={handleRemoveCategory}
                  searchPlaceholder="Pesquisar categorias..."
                  emptyMessage="Nenhuma categoria adicionada"
                  displayField={(category) => category.name}
                />
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/teams")}
                  disabled={updateTeam.isPending}
                  className="cursor-pointer"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={updateTeam.isPending}
                  isLoading={updateTeam.isPending}
                  className="text-white cursor-pointer"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {updateTeam.isPending ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            </CardContentUI>
          </Card>
        </form>
      </motion.div>
    </div>
  );
};
